import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  BenefitClaimView,
  EntityOperatorView,
  EntityProfileViewAttributes,
  ClaimPolicyView,
  EntityProfileView,
  HeldBenefitView,
  ResolvedTerm,
  WorkflowProgress,
} from '@/app/commons/generated'
import { HeldBenefitViewStateEnum } from '@/app/commons/generated'
import httpService from './httpService'
import { Response } from '@/types'

/**
 * The member's own side of benefits — what I hold and where I can claim it.
 *
 * Separate from `clubBenefits.ts`, which is the club's side. The two never share a call: a director
 * working their claim list and a member choosing where to claim are different people asking
 * different questions, and the permissions behind them are different too.
 *
 * ## The wire types are generated, not written here
 *
 * Everything the server defines comes from `@/app/commons/generated`, regenerated from its OpenAPI
 * document. Hand-mirroring them is how a client ends up with a shape the server stopped sending:
 * this file previously declared `terms` as an object when the server had already moved to an array,
 * and nothing caught it because both sides compiled. The re-exports below exist so callers import
 * from one place, not so the types are redefined.
 */

export type { EntityProfileViewAttributes, ClaimPolicyView, ResolvedTerm, WorkflowProgress }

/**
 * ## Fields the server sends that the generated client has not caught up with
 *
 * Each type below is the generated shape PLUS one optional field, never a redefinition — the
 * warning above stands, and an intersection cannot drift from the server the way a hand-written
 * copy does: everything except the added field still comes from the generated model.
 *
 * Every one of these goes away the next time the client is regenerated against a server carrying
 * them, and the callers keep compiling because each field is optional.
 *
 * Optional is also the honest shape today: a client can be talking to a server from before the
 * field existed, and one that cannot tell what it is holding should render the plain text and link
 * nowhere rather than guess.
 */

/** `targetKind` decides which page a claim links to: `club`, or an entity kind's key. */
export type BenefitClaimViewWithKind = BenefitClaimView & { targetKind?: string }

/**
 * One line of redemption copy, as the server resolved it for this claim.
 *
 * `text` is always renderable, so a screen can ignore everything else and still be correct.
 * `i18nKey` is the better path: the copy the server ships by default has a translated phrase under
 * that name in our own locale files, so a Spanish reader gets Spanish rather than the server's
 * English. The key is namespace-free — the server cannot know we file these under `benefits.` and
 * mobile files them under `common.` — so `redemptionText` adds the prefix.
 */
export interface ResolvedText {
  text: string
  /** Empty when there is no key — never null. The server sends a value for every field. */
  i18nKey: string
  args: Record<string, string>
}

/**
 * How to redeem one claimed benefit — the whole instruction block, decided by the server.
 *
 * This replaces three sentences that were hard-coded on this page and again in the mobile app,
 * identical in both and true only of a club front desk. A benefit now says how it is redeemed, so
 * an online booking link or a scannable code needs no client release.
 *
 * Absent in exactly two cases, and both are the server declining to say something untrue: while the
 * benefit is unclaimed (the steps name a target and none has been chosen) and while a claim is
 * still waiting on the target to accept. A card therefore renders this when it is there and shows
 * nothing when it is not — it must NOT reinstate a default, which is the hard-coding coming back.
 */
export interface ResolvedRedemption {
  kind: string
  title: ResolvedText
  steps: ResolvedText[]
  /** Empty when there is no small print. */
  footnote: ResolvedText
  /** Already rendered — never a template. Empty for a method with no link. */
  url: string
  linkLabel: ResolvedText
  /**
   * What the member shows at the counter, in the order it should be drawn.
   *
   * Empty for an online benefit, and for an in-person one whose target already knows who is
   * standing there. More than one entry is a club mid-rollout showing a readable identity AND a
   * code — which is why this is a list and not a flag.
   */
  credentials: ResolvedCredential[]
}

/** One labelled value inside a credential block — "DUPR ID", "XOME32". */
export interface CredentialField {
  label: ResolvedText
  value: string
}

/**
 * What the member shows at the counter, resolved for this claim.
 *
 * A renderer switches on `kind` to pick a layout — rows of text, or a code — and takes the rest as
 * given. A kind it has never heard of draws nothing rather than guessing.
 */
export interface ResolvedCredential {
  kind: string
  label: ResolvedText
  /** Identity credentials only. */
  fields: CredentialField[]
  /**
   * Scan credentials only: what to encode in the QR, **verbatim**.
   *
   * The grammar is the server's and will change when the staff-side scanner ships. Nothing here
   * reads it; it goes straight into the encoder.
   */
  payload: string
}

/**
 * The server's redemption discriminators, named once so no screen misspells one.
 *
 * Open from a client's point of view: a build that meets a kind it has never heard of must still
 * render the title, steps and footnote, because those are on every method. Only the extras — a
 * link, a code — are kind-specific.
 */
export const RedemptionKinds = {
  InPerson: 'in-person',
  Online: 'online',
  /**
   * Nothing to say yet — unclaimed, or waiting on the target to accept.
   *
   * The server's null object, not an authorable method. See {@link NO_REDEMPTION}.
   */
  None: 'none',
} as const

/**
 * What the member shows at the counter. There is no separate "scan" METHOD — scanning is one way of
 * presenting an in-person claim, and a club mid-rollout shows both of these at once.
 */
export const RedemptionCredentialKinds = {
  Identity: 'identity',
  ScanCode: 'scan-code',
} as const

/** `redemptionKind` says how claiming under this policy would be redeemed. */
export type ClaimPolicyViewWithRedemption = ClaimPolicyView & { redemptionKind?: string | null }

/**
 * A benefit with nothing to say about redeeming it yet — unclaimed, or waiting on the target.
 *
 * The client's copy of the server's own null object, for the one case the server cannot cover: a
 * deploy talking to an API from before `redemption` existed. Normalizing there, once, is what lets
 * every component below treat the field as always present.
 *
 * Empty rather than apologetic. A renderer handed no steps draws nothing without being told to,
 * which is the whole reason this is a value and not a null.
 */
export const NO_TEXT: ResolvedText = { text: '', i18nKey: '', args: {} }

export const NO_REDEMPTION: ResolvedRedemption = {
  kind: RedemptionKinds.None,
  title: NO_TEXT,
  steps: [],
  footnote: NO_TEXT,
  url: '',
  linkLabel: NO_TEXT,
  credentials: [],
}

/**
 * A partner where an unclaimed benefit can be used, and how.
 *
 * A partner benefit is never claimed in the app: the member follows the partner's link, and the
 * partner claims it through the partner API. So this is concrete rather than a kind of target to
 * pick — each partner, what the benefit is worth there, and the link, already rendered for this
 * member. Empty once the benefit is claimed anywhere.
 */
export interface PartnerRedemptionView {
  /** Obfuscated, like every id the server hands out. */
  partnerUrn: string
  partnerName: string
  /** What the partner calls what it gives. */
  benefitLabel: string
  /** What this benefit is worth there, capped by what the member holds. */
  terms: ResolvedTerm[]
  /** The server's sentence for `terms`; show verbatim. Absent from older servers. */
  worth?: string
  /** The partner's link and instructions; the null object when it authored none. */
  redemption: ResolvedRedemption
}

/**
 * One part of a bundle made of several benefits, and where it stands — a leaf (one benefit) or a
 * group of them ("all of these", "one of these"), nested as the bundle was authored.
 *
 * A leaf carries its OWN held benefit: its own grant, claim options, claim and instructions. That
 * grant is what a claim of this part spends, and it also arrives as a row of its own in the list —
 * see `visibleBenefits`, which shows it inside its bundle instead.
 */
export interface BundlePartView<B = HeldBenefit> {
  /** The part's id within its bundle. */
  id: string
  kind: 'benefit' | 'all-of' | 'one-of' | string
  /** What the member reads: the benefit's name, or the server's words for a group. */
  label: string
  /**
   * A leaf: `AVAILABLE`, `CLAIMED`, `RESERVED`, `USED`, `LOCKED` (another choice in its group was
   * taken) or `EXPIRED`. A group: `AVAILABLE`, `IN_PROGRESS`, `COMPLETE`, `LOCKED` or `EXPIRED`.
   */
  state: string
  /** Whether this part can be claimed or used right now. Never true of a group. */
  claimable: boolean
  benefitUrn?: string | null
  max?: number | null
  /** A leaf's own held benefit; null for a group, or when the part could not be read. */
  benefit?: B | null
  children: BundlePartView<B>[]
}

/** The kinds of part, as the server names them. */
export const PartKinds = {
  Benefit: 'benefit',
  AllOf: 'all-of',
  OneOf: 'one-of',
} as const

export type HeldBenefit = Omit<HeldBenefitView, 'claim' | 'claimPolicies'> & {
  claim?: BenefitClaimViewWithKind
  claimPolicies: ClaimPolicyViewWithRedemption[]
  /** Always present (empty when none), normalized in {@link withRedemption}. */
  partners: PartnerRedemptionView[]
  /**
   * Always present — see {@link withRedemption}, which is the only place this is allowed to be
   * absent and the only place that fact is handled.
   */
  redemption: ResolvedRedemption
  /**
   * A bundle made of several benefits: its parts and where each stands. Absent for a single
   * benefit. Such a bundle is never claimed itself — its parts are — so it has no claim options of
   * its own; see `claimOptions`.
   */
  parts?: BundlePartView | null
}

/**
 * A held benefit **as it arrives**, where the API may be older than this field.
 *
 * Deliberately a separate type rather than an optional field on {@link HeldBenefit}. One optional
 * would push the same `?? NO_REDEMPTION` into every component that reads it, and the components are
 * exactly where it gets forgotten; naming the wire shape confines the uncertainty to the fetch.
 */
export type HeldBenefitWire = Omit<HeldBenefit, 'redemption' | 'partners' | 'parts'> & {
  redemption?: ResolvedRedemption | null
  /** Absent on a server from before partner benefits. */
  partners?: Array<Omit<PartnerRedemptionView, 'redemption'> & { redemption?: ResolvedRedemption | null }> | null
  /** Each leaf's benefit arrives in the same wire shape as a row, and is normalized the same way. */
  parts?: BundlePartView<HeldBenefitWire> | null
}

/** A part tree off the wire, with every leaf's benefit normalized as a row is. */
const partsOf = (part: BundlePartView<HeldBenefitWire>): BundlePartView => ({
  ...part,
  benefit: part.benefit ? withRedemption(part.benefit) : null,
  children: (part.children ?? []).map(partsOf),
})

/** Normalize one benefit off the wire. The only null check for redemption in the app. */
export const withRedemption = (benefit: HeldBenefitWire): HeldBenefit => ({
  ...benefit,
  redemption: benefit.redemption ?? NO_REDEMPTION,
  partners: (benefit.partners ?? []).map((partner) => ({
    ...partner,
    terms: partner.terms ?? [],
    redemption: partner.redemption ?? NO_REDEMPTION,
  })),
  parts: benefit.parts ? partsOf(benefit.parts) : null,
})

/** `imageUrl` is the operator's photo — an entity has none of its own and should not grow one. */
export type EntityOperatorViewWithImage = EntityOperatorView & { imageUrl?: string | null }

export type CoachProfileView = Omit<EntityProfileView, 'operator'> & {
  operator?: EntityOperatorViewWithImage
}

export type { BenefitClaimView, EntityOperatorView, EntityProfileView, HeldBenefitView }

/** The claimable state, as the server enumerates it — not a union retyped by hand. */
export const BenefitState = HeldBenefitViewStateEnum
export type BenefitState = HeldBenefitViewStateEnum

/** Kept as local aliases so the many call sites need no rename. */
export type CoachProfile = CoachProfileView
export type CoachOperator = EntityOperatorView

export interface BenefitTargetClub {
  clubId: number
  clubName: string
  shortAddress: string | null
  /** The club's image. Named for the wire field — it is `mediaUrl`, not `imageUrl`. */
  mediaUrl: string | null
}

/** One coach a member could claim at, and what the claim would be worth with them. */
export interface BenefitTargetCoach {
  /** Obfuscated, and passed back to `assign` verbatim — the client never builds an entity URN. */
  targetUrn: string
  entityKind: string
  /** The operating player, so the row can link to a profile the member already knows how to read. */
  playerId: number
  name: string
  imageUrl: string | null
  shortAddress: string | null
  headline: string | null
  terms: ResolvedTerm[]
  /** The server's sentence for `terms`; show verbatim. Absent when this coach settles nothing. */
  worth?: string
}

/**
 * One page of claim targets.
 *
 * `nextOffset` is an opaque cursor, not an item index: the server filters targets by acceptance
 * rules *after* the search index has paged, so a position in the result list does not describe a
 * position the index can resume from. Pass it back verbatim; null means there are no more.
 */
export interface BenefitTargetPage {
  clubs: BenefitTargetClub[]
  nextOffset: number | null
}

export interface BenefitCoachTargetPage {
  coaches: BenefitTargetCoach[]
  nextOffset: number | null
}

/**
 * A chosen target, in the one shape the confirm step and the mutation both need.
 *
 * `kind` exists because the two lists are genuinely different reads with different row shapes, and
 * everything downstream of selection — the confirm sentence, the invalidation, the success copy —
 * needs to know which it is holding without re-deriving it from the URN string.
 */
export type BenefitTarget =
  | { kind: 'club'; id: number; name: string; urn: string }
  | { kind: 'coach'; id: number; name: string; urn: string }

export const MyBenefitQueryKeys = {
  mine: ['my-benefits'] as const,
  /** Prefix covering every target page for every grant — what a claim invalidates. */
  allTargets: ['my-benefit-targets'] as const,
  /**
   * `null` is part of the key, not a hole to plug with `0`.
   *
   * These queries are disabled until the id is known, so their key describes a request that was
   * never made. A stand-in `0` names a cache entry indistinguishable from grant 0's, and reads as a
   * real id to anyone debugging the cache.
   */
  targets: (grantId: number | null) => ['my-benefit-targets', 'clubs', grantId] as const,
  coachTargets: (grantId: number | null) => ['my-benefit-targets', 'coaches', grantId] as const,
  claimableAt: (clubId: number | null) => ['my-benefits-claimable-at', clubId] as const,
  coachProfile: (kind: string | null, id: number | null) => ['coach-profile', kind, id] as const,
  /**
   * Not under `claimableAt`, and not invalidated by a claim. What a club takes part in does not
   * change because this member spent something there — sharing a prefix would throw the answer
   * away on every claim and refetch it for no reason.
   */
  clubPrograms: (clubId: number | null) => ['club-benefit-programs', clubId] as const,
}

/**
 * The `Result` envelope these endpoints return. `results` is always present — the server's field is
 * a non-null list, so an empty answer is `[]` rather than an absent key.
 */
type ResultsOf<T> = Response & { results: T[] }

const base = '/benefits/v1.0'

export const useMyBenefits = (enabled = true) =>
  useQuery({
    queryKey: MyBenefitQueryKeys.mine,
    enabled,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<ResultsOf<HeldBenefitWire>>(`${base}/mine`, { signal })
      return res.results.map(withRedemption)
    },
  })

/** The query string both pickers send: an optional position, an optional search, and the cursor. */
const targetParams = (
  position: { lat: number; lng: number } | null,
  query: string,
  pageParam: unknown,
  entityKind?: string
) => {
  const params = new URLSearchParams()
  if (entityKind) {
    params.set('kind', entityKind)
  }
  if (position) {
    params.set('latitude', String(position.lat))
    params.set('longitude', String(position.lng))
  }
  if (query) {
    params.set('query', query)
  }
  // Omitted on the first page: the server treats a missing offset as "start", and sending an
  // explicit 0 would be a second way to say the same thing.
  if (pageParam !== undefined && pageParam !== null) {
    params.set('offset', String(pageParam))
  }
  const search = params.toString()
  return search ? `?${search}` : ''
}

/**
 * Clubs where this benefit can be claimed — the member's own clubs first, then by distance.
 *
 * That ordering is the server's, not ours: it spans pages, so re-sorting a page here would only
 * shuffle whichever clubs happened to arrive together.
 */
export const useBenefitTargets = (
  grantId: number | null,
  position: { lat: number; lng: number } | null,
  query: string,
  enabled = true
) =>
  useInfiniteQuery({
    queryKey: [...MyBenefitQueryKeys.targets(grantId), position?.lat ?? null, position?.lng ?? null, query],
    enabled: enabled && grantId !== null,
    queryFn: async ({ signal, pageParam }) => {
      // `enabled` keeps this from running before the grant is known; the throw is what makes that a
      // guarantee rather than a request to `/benefits/v1.0/null/targets`.
      if (grantId === null) throw new Error('Cannot list benefit targets before the grant is known')
      const res = await httpService.Get<ResultsOf<BenefitTargetPage>>(
        `${base}/${grantId}/targets${targetParams(position, query, pageParam)}`,
        { signal }
      )
      return res.results[0] ?? { clubs: [], nextOffset: null }
    },
    // The cursor is opaque — pass it back verbatim. Returning `undefined` is what tells React Query
    // there is no next page; a null cursor means the server has run out.
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  })

/**
 * Coaches where this benefit can be claimed, nearest first.
 *
 * A separate call from the club picker rather than one list with a kind flag, because the server
 * paged them separately and for a reason: clubs are ordered own-clubs-first over a club index and
 * coaches by distance over the player index. Merging them client-side would interleave two
 * orderings that each only make sense within themselves.
 *
 * Asked only for a kind the benefit's own claim options name, and only while that option's tab is
 * showing — see `claimOptions`.
 */
export const useBenefitCoachTargets = (
  grantId: number | null,
  position: { lat: number; lng: number } | null,
  query: string,
  /** The entity kind to list, straight from the benefit's own claim policy. */
  entityKind: string,
  enabled = true
) =>
  useInfiniteQuery({
    queryKey: [
      ...MyBenefitQueryKeys.coachTargets(grantId),
      entityKind,
      position?.lat ?? null,
      position?.lng ?? null,
      query,
    ],
    // Never asked without a kind: the server would answer for a kind of its own choosing, and the
    // kind is the benefit's to name.
    enabled: enabled && grantId !== null && !!entityKind,
    queryFn: async ({ signal, pageParam }) => {
      if (grantId === null) throw new Error('Cannot list benefit coaches before the grant is known')
      if (!entityKind) throw new Error('Cannot list benefit coaches before the kind is known')
      const res = await httpService.Get<ResultsOf<BenefitCoachTargetPage>>(
        // The kind is the policy's, not a default: a benefit may name `club-coach` where another
        // names `independent-coach`, and the endpoint lists one kind at a time.
        `${base}/${grantId}/coaches${targetParams(position, query, pageParam, entityKind)}`,
        { signal }
      )
      return res.results[0] ?? { coaches: [], nextOffset: null }
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  })

/**
 * What, if anything, this member can claim at one specific club.
 *
 * Lives under the club rather than under benefits because that is the question being asked: the
 * caller is looking at a club. An empty list means no claim affordance, and deliberately does not
 * say why — not held, club not participating, and club rules reject you all look the same from
 * here. A member who fails a club's eligibility rule should not learn the rule exists.
 */
export const useClaimableAtClub = (clubId: number | null, enabled = true) =>
  useQuery({
    queryKey: MyBenefitQueryKeys.claimableAt(clubId),
    enabled: enabled && clubId !== null,
    queryFn: async ({ signal }) => {
      // `enabled` keeps this from running before the club is known; the throw is what makes that a
      // guarantee rather than a URL reading `/club/null/benefits/v1.0`.
      if (clubId === null) throw new Error('Cannot check claimable benefits before the club is known')
      const res = await httpService.Get<ResultsOf<HeldBenefitWire>>(`/club/${clubId}/benefits/v1.0`, { signal })
      return res.results.map(withRedemption)
    },
  })

/** Just the benefits that can still be claimed — what eligibility marking keys off. */
export const useClaimableBenefits = (enabled = true) => {
  const query = useMyBenefits(enabled)
  return {
    ...query,
    data: query.data?.filter((benefit) => benefit.state === BenefitState.Available),
  }
}

/**
 * One benefit program a club takes part in.
 *
 * A fact about the CLUB: answered identically for every caller, whatever they hold. That is what
 * separates it from {@link useClaimableAtClub}, which folds in holdings and eligibility and comes
 * back empty for most members — a list that would contradict the badge that opened it.
 *
 * `classUrn` is the bundle class, the same URN a held benefit carries as its `bundleUrn`, so a
 * client can line a club's programs up against what the member holds without asking the server a
 * caller-specific question.
 */
export type ClubBenefitProgram = {
  classUrn: string
  label: string
  terms: ResolvedTerm[]
  /** The server's sentence for what a claim here is worth; show verbatim. */
  worth?: string
}

/**
 * What a club takes part in. Independent of the caller, so it is cached per club and shared by
 * every member who looks at it.
 *
 * `enabled` lets a caller hold the request until it is worth making — the browse overlay only asks
 * once opened, which is the difference between one request and one per visible row.
 */
export const useClubBenefitPrograms = (clubId: number | null, enabled = true) =>
  useQuery({
    queryKey: MyBenefitQueryKeys.clubPrograms(clubId),
    enabled: enabled && clubId !== null,
    // A club joins or leaves a program about never, and the answer is the same for everyone, so
    // re-asking on every mount buys nothing.
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      if (clubId === null) throw new Error('Cannot load club benefits before the club is known')
      const res = await httpService.Get<ResultsOf<ClubBenefitProgram>>(`/club/${clubId}/benefits/v1.0/programs`, {
        signal,
      })
      return res.results
    },
  })

/**
 * A coach's public profile.
 *
 * Keyed by kind and id because that is how the entity platform addresses anything: `independent-coach`
 * and `club-coach` are different kinds that can share an id, so the pair is the identity and the id
 * alone is not.
 */
export const useCoachProfile = (kind: string | null, entityId: number | null) =>
  useQuery({
    queryKey: MyBenefitQueryKeys.coachProfile(kind, entityId),
    enabled: kind !== null && entityId !== null,
    queryFn: async ({ signal }) => {
      if (kind === null || entityId === null) throw new Error('Cannot load a profile before the entity is known')
      const res = await httpService.Get<ResultsOf<CoachProfile>>(`/entities/${kind}/${entityId}/profile`, { signal })
      return res.results[0] ?? null
    },
  })

/**
 * The target kind the server uses for clubs.
 *
 * Clubs are not platform entities, so they are the one kind whose targets come from a different
 * endpoint — which is why this name is needed at all rather than every kind being uniform.
 */
export const CLUB_TARGET_KIND = 'club'

/**
 * The target kind the server uses for partners. A partner has no page in this app — the member
 * reaches it through the redemption link — so a claim at one never links anywhere in-app.
 */
export const PARTNER_TARGET_KIND = 'partner'

/** How every club target URN starts — what tells a club selection from a coach's. */
export const clubTargetUrnPrefix = 'urn:dupr:club:'

/** The URN naming a club as a claim target. Coaches carry their own and are never built here. */
export const clubTargetUrn = (clubId: number) => `${clubTargetUrnPrefix}${clubId}`

export const useClaimBenefit = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ grantId, target }: { grantId: number; target: BenefitTarget }) =>
      httpService.Post(`${base}/${grantId}/assign`, { targetUrn: target.urn }),
    onSuccess: (_data, { target }) => {
      // Claiming is final from the member's side, so everything that showed this benefit as
      // available is now stale: the held list, the club's own button, and any target list.
      queryClient.invalidateQueries({ queryKey: MyBenefitQueryKeys.mine })
      if (target.kind === 'club') {
        queryClient.invalidateQueries({ queryKey: MyBenefitQueryKeys.claimableAt(target.id) })
      }
      queryClient.invalidateQueries({ queryKey: MyBenefitQueryKeys.allTargets })
    },
  })
}
